import { useCallback, useEffect, useState } from "react";
import { API } from "api/API";
import { ApolloError, useMutation, useQuery, useSubscription } from "@apollo/client";
import {
    insertMachineCenterCorporateBO,
    getBottleneckMachineCenter,
    updateMachineCenter,
    getMachineCenterDetailsBO,
    subscribeMachineCenterDetailsHMICodeBO,
    getMachineCenterPlcAddress,
    getMachineCenterCurrentStatus,
    insertMachineCenterLog,
    setMachineCentersClusterId,
    getAllMachineCenters,
    getAllFunctionalLocations,
    setMachineCentersOrderByProductionLine,
    GetMachineCenterFailureDetailsInfoBO,
    upsertMachineCenterEmailNotificationSetting,
} from "../../api/queries/machineCenterAPI";
import {
    IMachineCentersEntity,
    IMachineCenterDetailsBO,
    IMachineCenterCurrentStatus,
    IMachineCenterFailureDetailsInfoBO,
    IProductionLineOptions,
} from "./MachineCentersInterfaces";
import { IMachineCenter } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { IFunctionalLocation } from "entities/FailureDetailsEntity/FailureDetailsEntityInterfaces";
import { getExistingEvents, getOngoingDowntimeEvent } from "api/queries/eventReportsAPI";
import { getProductionLinesOptionsBO } from "api/queries/productionLinesAPI";

export const MachineCentersEntity: IMachineCentersEntity = {
    InsertMachineCentersCorporateBO: () => {
        const [addMachineCenter, { error }] = useMutation(insertMachineCenterCorporateBO);

        return { apolloMutationError: error, addMachineCenter };
    },
    desvinculateMachineCenterBottleneck: async (productionLineId) => {
        try {
            const apolloClient = new API().client;
            const responseGet = await apolloClient.query({
                query: getBottleneckMachineCenter,
                variables: { productionLineId },
            });
            const machineCenter: { machine_center_id: number }[] = responseGet?.data?.machine_center || [];
            if (machineCenter.length > 0) {
                const responseUpdate = await apolloClient.mutate({
                    mutation: updateMachineCenter,
                    variables: {
                        machineCenterId: machineCenter[0].machine_center_id,
                        changes: {
                            is_bottleneck: false,
                        },
                    },
                });
                return responseUpdate;
            } else {
                return;
            }
        } catch {
            return;
        }
    },
    uniquePlcAddressByMill: async (millId, plcAddress, machineCenterId) => {
        try {
            const apolloClient = new API().client;
            const responseGet = await apolloClient.query({
                query: getMachineCenterPlcAddress,
                variables: {
                    where: {
                        ...(machineCenterId
                            ? {
                                  machine_center_id: { _neq: machineCenterId },
                              }
                            : {}),
                        production_line: { mill_id: { _eq: millId } },
                        plc_address: { _eq: plcAddress },
                    },
                },
            });
            const machineCenter: { machine_center_id: number; name: string }[] =
                responseGet?.data?.machine_center || [];
            if (machineCenter.length > 0) {
                return { unique: false, machineCenterName: machineCenter[0].name };
            } else {
                return { unique: true, machineCenterName: "" };
            }
        } catch {
            return { unique: false, machineCenterName: "" };
        }
    },
    GetMachineCenterDetailsBO: (machineCenterId) => {
        const { loading, error, data, refetch } = useQuery(getMachineCenterDetailsBO, {
            variables: { machine_center_id: machineCenterId },
        });
        const [machineCenter, setMachineCenter] = useState<IMachineCenterDetailsBO[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const machineCenterArray: IMachineCenterDetailsBO[] = data.machine_center;
                setMachineCenter(machineCenterArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, machineCenter, apolloQueryError, refetch };
    },
    SubscribeMachineCenterHMICode: (machineCenterId) => {
        const { loading, error, data } = useSubscription(subscribeMachineCenterDetailsHMICodeBO, {
            variables: { machine_center_id: machineCenterId },
        });
        const [machineCenter, setMachineCenter] = useState<IMachineCenterDetailsBO[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const machineCenterArray: IMachineCenterDetailsBO[] = data.machine_center;
                setMachineCenter(machineCenterArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, machineCenter, apolloQueryError };
    },
    UpdateMachineCenterCorporateBO: () => {
        const [updateMachineCenterMutation, { error, loading }] = useMutation(updateMachineCenter);

        return { apolloMutationError: error, updateMachineCenterMutation, loadingUpdate: loading };
    },
    getMachineCenterCurrentStatus: async (machineCenterIds) => {
        try {
            const apolloClient = new API().client;
            const response: {
                data: {
                    event_report: IMachineCenterCurrentStatus[];
                };
            } = await apolloClient.query({
                query: getMachineCenterCurrentStatus,
                variables: { machine_center_ids: machineCenterIds, limit: machineCenterIds.length },
            });

            if (response?.data?.event_report) {
                return response.data.event_report;
            } else {
                return [];
            }
        } catch (error) {
            console.error("An error occurred while fetching machine center current status:", error);
            return [];
        }
    },
    InsertMachineCenterLog: () => {
        const [insertHMILog, { error }] = useMutation(insertMachineCenterLog);

        return { apolloMutationError: error, insertHMILog };
    },
    GetAllMachineCenters: () => {
        const { loading, error, data, refetch } = useQuery(getAllMachineCenters);

        const [machineCenters, setMachineCenters] = useState<IMachineCenter[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const machineCenterArray: IMachineCenter[] = data.machine_center;
                setMachineCenters(machineCenterArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, machineCenters, apolloQueryError, refetch };
    },
    GetAllFunctionalLocations: () => {
        const { loading, error, data, refetch } = useQuery(getAllFunctionalLocations);

        const [functionalLocations, setFunctionalLocations] = useState<IFunctionalLocation[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const functionalLocationArray: IFunctionalLocation[] = data.functional_location;
                setFunctionalLocations(functionalLocationArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, functionalLocations, apolloQueryError, refetch };
    },
    SetMachineCentersClusterId: () => {
        const [setHMIsClusterId, { error, loading }] = useMutation(setMachineCentersClusterId);

        return { apolloMutationError: error, setHMIsClusterId, loadingUpdate: loading };
    },
    SetMachineCentersOrderByProductionLine: () => {
        const [setMachineCentersOrder, { error, loading }] = useMutation(setMachineCentersOrderByProductionLine);

        return { apolloMutationError: error, setMachineCentersOrder, loadingOrderUpdate: loading };
    },
    CheckMachineCenterOngoingEvent: () => {
        const validateOngoingEvent = useCallback(async (machineCenterId: number) => {
            try {
                const apolloClient = new API().client;
                const responseGet = await apolloClient.query({
                    query: getOngoingDowntimeEvent,
                    variables: { machineCenterId: machineCenterId },
                });
                const eventReport: number = responseGet?.data?.event_report_aggregate?.aggregate?.count || 0;
                if (eventReport > 0) {
                    return false;
                } else {
                    return true;
                }
            } catch {
                return false;
            }
        }, []);

        const validateExistingEvents = useCallback(async (machineCenterId: number) => {
            try {
                const apolloClient = new API().client;
                const responseGet = await apolloClient.query({
                    query: getExistingEvents,
                    variables: { machineCenterId: machineCenterId },
                });
                const eventReport: number = responseGet?.data?.event_report?.length || 0;
                if (eventReport > 0) {
                    return true;
                } else {
                    return false;
                }
            } catch {
                //In case of any error when querying is better to return as true to avoid adding the deleted_at field
                return true;
            }
        }, []);

        return { validateOngoingEvent, validateExistingEvents };
    },
    GetMachineCenterFailureDetailsInfoBO: (machineCenterId, millId) => {
        const [machineCenterFailureDetails, setMachineCenterFailureDetails] = useState<
            IMachineCenterFailureDetailsInfoBO[]
        >([]);
        const [productionLineOptions, setProductionLineOptions] = useState<IProductionLineOptions[]>([]);

        const { loading, error } = useQuery(GetMachineCenterFailureDetailsInfoBO, {
            skip: !machineCenterId,
            variables: { machine_center_id: machineCenterId },
            onCompleted: (data) => {
                if (data.machine_center) {
                    const machineCenterArray: IMachineCenterFailureDetailsInfoBO[] = data.machine_center;
                    setMachineCenterFailureDetails(machineCenterArray);
                }
            },
        });

        const { loading: loadingOptions, error: errorOptions } = useQuery(getProductionLinesOptionsBO, {
            skip: !millId,
            variables: { mill_id: millId },
            onCompleted: (data) => {
                if (data.production_line) {
                    const IProductionLineOptions: IProductionLineOptions[] = data.production_line;
                    setProductionLineOptions(IProductionLineOptions);
                }
            },
        });

        return {
            loading: loading || loadingOptions,
            machineCenterFailureDetails,
            productionLineOptions,
            error: error || errorOptions,
        };
    },
    UpsertMachineCenterNotificationSettingBO: () => {
        const [updateMachineCenterSettingMutation, { error, loading }] = useMutation(
            upsertMachineCenterEmailNotificationSetting,
        );

        return { apolloMutationError: error, updateMachineCenterSettingMutation, loading };
    },
};
